"use client";

import { createContext, useContext, type ReactNode } from "react";
import { EscalationBanner } from "@/components/notifications/EscalationBanner";
import { QuerySkeleton } from "@/components/ui/skeleton";
import type { CurrentTenantState, TenantMembershipSummary } from "@/core/auth/current-tenant";
import { FirstRunScreen } from "./FirstRunScreen";
import { useCurrentTenant } from "./use-current-tenant";

type TenantContextValue = CurrentTenantState & {
  selectTenant: (tenantId: string) => void;
};

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantContextProvider({ children }: { children: ReactNode }) {
  const currentTenant = useCurrentTenant();

  function selectTenant(tenantId: string) {
    window.localStorage.setItem("haulbase.selectedTenantId", tenantId);
    window.location.reload();
  }

  return (
    <TenantContext.Provider value={{ ...currentTenant, selectTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenantContext(): TenantContextValue {
  const value = useContext(TenantContext);
  if (!value) throw new Error("TenantContextProvider is required");
  return value;
}

export function TenantStateGate({
  children,
}: {
  children: (tenant: TenantMembershipSummary) => ReactNode;
}) {
  const tenantState = useTenantContext();

  if (tenantState.status === "loading") {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <QuerySkeleton surface="tenantWorkspace" />
      </main>
    );
  }

  if (tenantState.status === "none") {
    return <FirstRunScreen />;
  }

  return (
    <>
      <EscalationBanner tenantId={tenantState.tenant.tenantId} />
      {children(tenantState.tenant)}
    </>
  );
}

